// @ts-strict-ignore
import type { AviaryColorProps } from "aviary-tokens";
import { useTranslation } from "react-i18next";

import { Button, Arrange, Cell } from "@aviary";
import type { ButtonAviarySize } from "@aviary/components/Button/Button.types";
import { emersonRoutes } from "@emerson/data/emersonRoutes";
import { l } from "@emerson/locales/i18n";
import { useWholesaleCartQuery } from "@shared/data/queries/WholesaleCartQuery/WholesaleCart.query";
import { e2eAttributes } from "@shared/e2eAttributes";
import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import { useAnalyticsLogger } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import type { AnalyticsPayload } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useFlippers } from "@shared/hooks/useFlippers/useFlippers";
import { Size } from "@shared/types/sizes";

import * as styles from "./AddedToCartButton.styles";

type Props = {
  variantId: string;
  analyticsEntryPoint: AnalyticsPayload["entry_point"];
  showQuantityIndicator?: boolean;
  hasMargin?: boolean;
  isFullWidth?: boolean;
  isHorizontal?: boolean;
  buttonSize?: ButtonAviarySize;
  isText?: boolean;
} & AviaryColorProps;

const AddedToCartButton = ({
  variantId,
  analyticsEntryPoint,
  showQuantityIndicator = true,
  isColor = "info",
  hasMargin = true,
  isFullWidth,
  isHorizontal,
  buttonSize = Size.MEDIUM,
  isText,
}: Props) => {
  const { t } = useTranslation();
  const { tablet } = useBreakpoints();
  const [isAuthCaptureWholesaleEnabled] = useFlippers("auth_capture_wholesale_lighthouse");
  const isDesktop = tablet.greaterThan;
  const isVertical = !isHorizontal && isDesktop;

  const { loading, data } = useWholesaleCartQuery();
  const logAnalyticsEvent = useAnalyticsLogger();

  if (loading || !data.currentOffice.wholesaleCart) return null;

  const currentLineItem = data.currentOffice.wholesaleCart.lineItems.find(
    lineItem => lineItem.variant.id === variantId
  );

  const logStartCheckoutEvent = () => {
    const productCount = data.currentOffice.wholesaleCart.lineItems.length;
    const itemCount = data.currentOffice.wholesaleCart.lineItems.reduce(
      (sum, value) => sum + value.quantity,
      0
    );
    const skuList = data.currentOffice.wholesaleCart.lineItems.map(
      lineItem => lineItem.variant.sku
    );

    const statusOfProducts = data.currentOffice.wholesaleCart.lineItems.map(
      lineItem => lineItem.variant.availabilityStatus
    );

    logAnalyticsEvent({
      eventName: AnalyticsEvent.EMERSON_CHECKOUT_START,
      payload: {
        entry_point: analyticsEntryPoint,
        order_id: data.currentOffice.wholesaleCart.id,
        number_of_items: itemCount,
        number_of_products: productCount,
        sku_list: skuList,
        status_of_products: statusOfProducts,
        auth_and_capture: isAuthCaptureWholesaleEnabled,
        contains_hemp: data.currentOffice.wholesaleCart.lineItems.some(
          lineItem => lineItem.variant.product.containsCbd
        ),
      },
    });
  };

  if (!currentLineItem) return null;

  return (
    <Arrange
      spacing={isDesktop ? "small" : "xlarge"}
      isFullWidth={isFullWidth}
      isVertical={isVertical}
      isWrapped={!isHorizontal}
      alignment={!showQuantityIndicator ? "center" : null}
    >
      {!!showQuantityIndicator && (
        <Cell css={[styles.addedToCartBtn, hasMargin && styles.addedToCartBtnMargin]}>
          {t(l.sharedCommon.QuantityAdded, { quantity: currentLineItem.quantity })}
        </Cell>
      )}
      <Button
        to={emersonRoutes.checkout.review}
        onClick={logStartCheckoutEvent}
        isFullWidth={!isText}
        isText={isText}
        isColor={isColor}
        data-e2e={e2eAttributes.viewCartButton}
        size={buttonSize}
      >
        {t(l.sharedCommon.ViewCart)}
      </Button>
    </Arrange>
  );
};

export { AddedToCartButton };
